/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { parse as parseQuery, stringify } from 'query-string'

import { EuiCallOut, EuiCode } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AsyncRequestError } from '@modules/ui-types'
import { asAjaxRequestError } from '@modules/utils/ajax'
import { ssoLoginUrl } from '@modules/auth/urls'

import { messages } from './messages'

import type { ReactElement } from 'react'

interface Props {
  error: AsyncRequestError
  errorDescription?: string
  redirectTo?: string
  state?: string
  contextSwitchLocationState?: { [key: string]: string | boolean }
  fromLogin?: boolean
}

const SsoError = ({ error, errorDescription, redirectTo, state, fromLogin = true }: Props) => {
  const parsedState = parseQuery(String(state))
  const originLoginIdentifier =
    typeof parsedState.loginIdentifier === `string` ? parsedState.loginIdentifier : undefined
  const redirectToFromState =
    typeof parsedState.redirectTo === `string` ? parsedState.redirectTo : undefined

  const errorCode =
    typeof error === `string`
      ? `idp.${error}` // if an error is a string, it's from IdP/Okta
      : asAjaxRequestError(error)
          ?.body?.errors?.map((err) => err.code)
          ?.pop()
  const errorResponseMessage =
    typeof error === `string`
      ? null
      : asAjaxRequestError(error)
          ?.body?.errors?.map((err) => err.message)
          ?.pop()

  const linkBackToLogin = (
    <Link to={{ pathname: '/login', search: stringify({ redirectTo }) }}>
      <FormattedMessage {...messages.errorLoginLink} />
    </Link>
  )
  const linkBackToSsoInit = originLoginIdentifier ? (
    <Link
      to={{
        pathname: `${ssoLoginUrl()}/${originLoginIdentifier}`,
        search: stringify({ redirectTo: redirectToFromState }),
      }}
    >
      <FormattedMessage {...messages.pleaseTryAgain} />
    </Link>
  ) : (
    <FormattedMessage {...messages.pleaseTryAgain} />
  )

  let errorMessage: ReactElement | null = null

  const domainNotVerifiedRegex = /Transformed username '.+@(.+)' is invalid./
  const domainNotVerifiedMatches = errorDescription?.match(domainNotVerifiedRegex)
  const emailNotPresentRegex = /The following required property is missing: 'email'./

  if (errorCode === 'idp.access_denied' && domainNotVerifiedMatches?.[1]) {
    const values = {
      domain: <EuiCode>{domainNotVerifiedMatches[1]}</EuiCode>,
    }
    errorMessage = <FormattedMessage {...messages.domainNotVerified} values={values} />
  } else if (errorCode === 'idp.access_denied' && errorDescription?.match(emailNotPresentRegex)) {
    const values = {
      email: <EuiCode>email</EuiCode>,
    }
    errorMessage = <FormattedMessage {...messages.emailNotPresent} values={values} />
  } else if (errorCode && messages[errorCode]) {
    const values = { login: linkBackToLogin, pleaseTryAgain: fromLogin ? linkBackToSsoInit : '' }
    errorMessage = <FormattedMessage {...messages[errorCode]} values={values} />
  } else if (errorResponseMessage) {
    errorMessage = errorResponseMessage
  } else {
    errorMessage = <FormattedMessage {...messages.unknownError} />
  }

  return (
    <EuiCallOut data-test-id='sso-error' title={errorMessage} color='danger' iconType='error' />
  )
}

export default SsoError
